import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { errorText, Icon, Modal, PopItem, Popover, usePopover, useToast } from '../../ui/kit';
import { fetchBlob } from '../../api/client';
import { fmtDate, fmtReport, today } from '../../lib/format';
import { isArrow, slug, toCsv, type Cell, type ReportRow, type ReportTable } from './model';
import { APP_NAME } from '../../lib/brand';

function CellText({ value, currency, dashZero }: { value: Cell; currency: string; dashZero?: boolean | undefined }) {
  if (value === null) return null;
  if (isArrow(value)) {
    if (!value.arrow) return <span className="muted">—</span>;
    const title = value.tone === 'good' ? 'Better' : value.tone === 'bad' ? 'Worse' : 'Changed';
    return <span className={`var ${value.tone}`} title={title}><Icon name={value.arrow} size={16} /><span className="visually-hidden">{title}</span></span>;
  }
  if (typeof value === 'bigint') return value === 0n && dashZero ? <span className="muted">—</span> : <>{fmtReport(value, currency)}</>;
  return <>{value}</>;
}

function Label({ row, print }: { row: ReportRow; print?: boolean | undefined }) {
  return (
    <>
      {row.code && <><span className="muted num">{row.code}</span>&nbsp; </>}
      {row.label}
      {row.help && !print && <> <span className="help-dot" title={row.help} role="img" aria-label={row.help}>?</span></>}
      {row.secondary && <div className="secondary">{row.secondary}</div>}
    </>
  );
}

/** A report table in the prototype's fin (ledger) or ex-table (statement) style. */
export function ReportTableView({ table, currency, print }: { table: ReportTable; currency: string; print?: boolean }) {
  const navigate = useNavigate();
  // The printout drops arrow columns; the variance percentage stays.
  const shown = table.columns.map((c) => !(print && c.arrows));
  const span = shown.filter(Boolean).length;
  const cls = print ? 'pdf-table' : `${table.style === 'ex' ? 'ex-table' : 'fin wide'} ${table.className ?? ''}`;
  return (
    <table className={cls}>
      <thead>
        <tr>{table.columns.map((c, n) => shown[n] && <th key={n} className={c.align ?? ''} scope="col">{c.arrows ? <span className="visually-hidden">{c.label}</span> : c.label}</th>)}</tr>
      </thead>
      <tbody>
        {table.rows.map((r, i) => {
          if (r.kind === 'sec') {
            return table.style === 'ex'
              ? <tr key={i} className="ex-sec"><th colSpan={span} scope="colgroup">{r.label}</th></tr>
              : <tr key={i} className="grp"><td colSpan={span}>{r.label}</td></tr>;
          }
          const go = !print && r.to ? r.to : undefined;
          const amountLink = !print ? (r.amountTo ?? (table.style === 'ex' ? r.to : undefined)) : undefined;
          const labelLink = !print && table.style === 'fin' ? r.to : undefined;
          let linked = false;
          const kindCls = r.kind === 'row' ? '' : r.kind;
          return (
            <tr key={i} className={`${kindCls} ${go ? 'clickable' : ''}`} onClick={go ? (e) => { if (!(e.target as HTMLElement).closest('a')) navigate(go); } : undefined}>
              <td colSpan={r.labelSpan ? 1 + shown.slice(1, 1 + r.labelSpan).filter(Boolean).length : undefined} className={table.style === 'ex' && r.kind === 'ind' ? 'ex-label' : r.secondary ? 'wrap-cell' : ''}>{labelLink ? <Link className="acct-link" to={labelLink}><Label row={r} /></Link> : <Label row={r} print={print} />}</td>
              {r.cells.map((c, n) => {
                const col = table.columns[n + 1];
                if (shown[n + 1] === false || n < (r.labelSpan ?? 0)) return null;
                if (col?.arrows) return <td key={n} className="arrow-cell"><CellText value={c} currency={currency} /></td>;
                const isMoney = typeof c === 'bigint';
                const text = <CellText value={c} currency={currency} dashZero={table.dashZero} />;
                const link = amountLink && isMoney && !linked && !(c === 0n && table.dashZero);
                if (link) linked = true;
                const note = r.cellNotes?.[n];
                return <td key={n} className={`${col?.align ?? ''} ${isMoney ? 'num' : ''} ${col?.wrap ? 'wrap-text' : ''}`}>{link ? <Link className="ex-link" to={amountLink}>{text}</Link> : text}{note && <div className="secondary num">{note}</div>}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const CHEV_SM = <svg className="chev-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>;

/**
 * The API's PDF of the report on screen: GET /v1/reports/{key}.pdf with the screen's query.
 * `detail` says what the PDF holds beyond the period, such as its comparison columns.
 */
export interface ServerPdf { key: string; query: Record<string, string | null | undefined>; detail?: string | undefined }
/** pdf: when set, PDF export downloads the server's PDF; without it (group reports) it prints the preview. */
export interface ExportSource { title: string; business: string; period: string; table: ReportTable | null; currency: string; fileTag: string; pdf?: ServerPdf | null }

/** The API path of a report PDF, with empty parameters left out. */
export function pdfPath(pdf: ServerPdf): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(pdf.query)) if (v) q.set(k, v);
  const s = q.toString();
  return `/v1/reports/${pdf.key}.pdf${s ? `?${s}` : ''}`;
}

/** The Export button and menu from the prototype, opening the PDF or CSV dialog. */
export function ExportButton({ source }: { source: ExportSource }) {
  const pop = usePopover();
  const toast = useToast();
  const [format, setFormat] = useState<'pdf' | 'csv' | null>(null);
  const open = (f: 'pdf' | 'csv') => {
    if (!source.table || source.table.rows.length === 0) { toast('This report has nothing to export yet'); return; }
    setFormat(f);
  };
  return (
    <>
      <button className="btn" onClick={pop.toggle} aria-haspopup="true" aria-expanded="false"><Icon name="download" />Export{CHEV_SM}</button>
      {pop.anchor && (
        <Popover anchor={pop.anchor} onClose={pop.close} label="Export">
          <div className="pop-title">Export</div>
          <PopItem icon="doc" title="PDF" sub="Formatted for printing and sharing" onClick={() => open('pdf')} />
          <PopItem icon="chart" title="CSV" sub="For spreadsheets such as Excel or Google Sheets" onClick={() => open('csv')} />
        </Popover>
      )}
      {format && source.table && <ExportModal source={source as ExportSource & { table: ReportTable }} format={format} setFormat={setFormat} onClose={() => setFormat(null)} />}
    </>
  );
}

const PRINT_CSS = `
body { margin: 0; font-family: var(--font-ui); color: #1d1d1b; }
.pdf-sheet { padding: 0; font-size: 10.5px; }
.pdf-head { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 16px; line-height: 1.5; }
.pdf-title { font-size: 17px; font-weight: 700; margin-bottom: 2px; }
.pdf-muted, .muted, .secondary { color: #6b6a65; }
.pdf-table { width: 100%; border-collapse: collapse; }
.pdf-table th, .pdf-table td { padding: 4px 6px; border-bottom: 1px solid #ddd9cf; font-size: 10.5px; white-space: nowrap; text-align: left; }
.pdf-table th { color: #6b6a65; font-weight: 700; }
.pdf-table .r { text-align: right; }
.pdf-table tr.ex-sec th, .pdf-table tr.grp td { color: #1d1d1b; padding-top: 10px; font-weight: 700; }
.pdf-table tr.sub td, .pdf-table tr.grand td { font-weight: 700; }
.pdf-table tr.grand td { border-top: 1.5px solid #1d1d1b; }
.pdf-table tr.ind td:first-child { padding-left: 14px; }
.pdf-foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid #ddd9cf; color: #6b6a65; font-size: 9.5px; }
@page { size: letter portrait; margin: 0.6in; }`;

function ExportModal({ source, format, setFormat, onClose }: { source: ExportSource & { table: ReportTable }; format: 'pdf' | 'csv'; setFormat: (f: 'pdf' | 'csv') => void; onClose: () => void }) {
  const toast = useToast();
  const sheet = useRef<HTMLDivElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const name = `${slug(source.title)}${source.fileTag ? `-${slug(source.fileTag)}` : ''}`;
  const { csv, lines } = useMemo(() => toCsv(source.title, source.business, source.period, source.table), [source]);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [csv]);

  const copy = () => {
    const fallback = () => { textarea.current?.focus(); textarea.current?.select(); toast('The CSV is selected. Press ⌘C or Ctrl+C to copy it.'); };
    try { navigator.clipboard.writeText(csv).then(() => toast('CSV copied. Paste it into a spreadsheet.'), fallback); } catch { fallback(); }
  };
  // The server's PDF, saved through an object URL like the invoice PDF. The URL is kept so the
  // name can be selected again, and released when the dialog closes.
  const [busy, setBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [saved, setSaved] = useState<{ url: string; name: string } | null>(null);
  useEffect(() => () => { if (saved) URL.revokeObjectURL(saved.url); }, [saved]);
  const download = async () => {
    if (!source.pdf) return;
    setBusy(true);
    setPdfError('');
    try {
      const blob = await fetchBlob(pdfPath(source.pdf));
      const file = { url: URL.createObjectURL(blob), name: `${name}.pdf` };
      setSaved(file);
      const a = document.createElement('a');
      a.href = file.url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast(`${file.name} downloaded`);
    } catch (e) { setPdfError(`The PDF could not be made. ${errorText(e)}`); }
    setBusy(false);
  };
  const print = () => {
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w || !sheet.current) { toast(`Your browser blocked the print window. Allow pop-ups for ${APP_NAME} and try again.`); return; }
    w.document.title = name;
    const style = w.document.createElement('style');
    // The print window starts blank: give it the UI font variable and the web font, and print once Inter has loaded.
    style.textContent = `:root { --font-ui: ${getComputedStyle(document.documentElement).getPropertyValue('--font-ui')}; }${PRINT_CSS}`;
    w.document.head.appendChild(style);
    w.document.body.innerHTML = sheet.current.outerHTML;
    const go = () => { w.focus(); w.print(); };
    const font = document.querySelector<HTMLLinkElement>('link[rel="stylesheet"][href*="fonts.googleapis.com"]');
    if (!font) { go(); return; }
    const link = w.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = font.href;
    link.onload = () => { Promise.all([w.document.fonts.load('400 10.5px Inter'), w.document.fonts.load('700 10.5px Inter')]).then(go, go); };
    link.onerror = go;
    w.document.head.appendChild(link);
  };

  let body: ReactNode;
  if (format === 'pdf') {
    body = (
      <>
        <div className="pdf-frame">
          <div className="pdf-sheet" ref={sheet}>
            <div className="pdf-head">
              <div><div className="pdf-title">{source.title}</div><div>{source.business}</div><div className="pdf-muted">{source.period}</div></div>
              <div className="pdf-muted" style={{ textAlign: 'right' }}>Prepared {fmtDate(today())}</div>
            </div>
            <ReportTableView table={source.table} currency={source.currency} print />
            <div className="pdf-foot">{source.business} · {source.title} · generated by {APP_NAME}</div>
          </div>
        </div>
        {source.pdf
          ? <>
            <p className="pdf-line" aria-live="polite"><Icon name="doc" size={16} /><span><b>{name}.pdf</b> · {[source.title, source.period, source.pdf.detail].filter(Boolean).join(' · ')} · US Letter</span></p>
            <p className="muted" style={{ fontSize: 12.5, textWrap: 'pretty', margin: 0 }}>{APP_NAME} draws the PDF from the same figures as this report, with every column shown here except the arrows. Wide reports turn landscape, and long ones run onto numbered pages with the headings repeated.</p>
            {pdfError && <div className="notice" role="alert" style={{ margin: 0 }}><Icon name="alert" size={17} /><div>{pdfError}</div></div>}
            {saved && <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Downloaded <a className="link-btn" href={saved.url} download={saved.name}>{saved.name}</a>. If your browser did not save it, select the name to save it again.</p>}
          </>
          : <p className="muted" style={{ fontSize: 12.5, textWrap: 'pretty' }}>US Letter, portrait, with every column shown on screen. Print opens your browser’s print dialog, where you can choose Save as PDF to keep <b>{name}.pdf</b>.</p>}
      </>
    );
  } else {
    body = (
      <>
        <div className="field">
          <label htmlFor="csv-text">{name}.csv · {lines} rows</label>
          <textarea ref={textarea} className="input csv-text" id="csv-text" readOnly spellCheck={false} value={csv} />
          <span className="hint">Amounts are plain numbers with no currency signs, so a spreadsheet can total them. Negative amounts start with a minus.</span>
        </div>
      </>
    );
  }
  return (
    <Modal title={`Export ${source.title.toLowerCase()}`} onClose={onClose} wide
      footer={<>
        <span className="spacer" />
        <button type="button" className="btn ghost" onClick={onClose}>Close</button>
        {format === 'csv'
          ? <>{url && <a className="btn" href={url} download={`${name}.csv`}><Icon name="download" />Download CSV</a>}<button type="button" className="btn primary" onClick={copy}><Icon name="check" />Copy CSV</button></>
          : <><button type="button" className="btn" onClick={() => setFormat('csv')}>Switch to CSV</button>{source.pdf
            ? <button type="button" className="btn primary" onClick={() => void download()} disabled={busy}><Icon name="download" />{busy ? 'Preparing PDF…' : 'Download PDF'}</button>
            : <button type="button" className="btn primary" onClick={print}><Icon name="download" />Print or save as PDF</button>}</>}
      </>}>
      <div className="ledger-export">
        <div className="tabs" role="tablist" style={{ alignSelf: 'flex-start' }}>
          {(['pdf', 'csv'] as const).map((k) => <button type="button" key={k} className={`tab ${format === k ? 'on' : ''}`} role="tab" aria-selected={format === k} onClick={() => setFormat(k)}>{k.toUpperCase()}</button>)}
        </div>
        {body}
      </div>
    </Modal>
  );
}
